"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "motion/react";

// useLayoutEffect ne doit pas s'exécuter lors du rendu serveur.
const useIsomorphicLayoutEffect =
    typeof window !== "undefined" ? useLayoutEffect : useEffect;

/**
 * Modale "bottom sheet" partagée.
 *
 * Comportement voulu partout dans Believemy : sur mobile, la modale glisse
 * depuis le bas de l'écran (feuille ancrée en bas, coins arrondis en haut,
 * poignée de drag) ; à partir de `sm` elle est centrée et bornée en largeur.
 *
 * Le composant encapsule l'overlay (fond assombri + flou), l'animation
 * d'entrée/sortie, la fermeture au clic extérieur et à la touche Échap, ainsi
 * que le verrouillage du scroll de la page. Le contenu de la modale est fourni
 * via `children` ; la fermeture (bouton X, reset d'états...) reste à la charge
 * de l'appelant via `onClose`.
 *
 * @param {boolean} isOpen        Ouvre / ferme la modale.
 * @param {() => void} onClose    Appelé sur clic extérieur ou touche Échap.
 * @param {string} maxWidthClass  Largeur desktop (défaut `sm:max-w-md`).
 * @param {string} panelClassName Classes additionnelles sur le panneau.
 * @param {boolean} showHandle    Affiche la poignée de drag (mobile). Défaut true.
 * @param {boolean} closeOnBackdrop Ferme au clic sur le fond. Défaut true.
 * @param {string} zIndexClass    Classe z-index de l'overlay (défaut `z-50`).
 * @param {string} labelledBy     Identifiant du titre affiché dans la modale.
 *                                Sert d'`aria-labelledby` : sans lui, un lecteur
 *                                d'écran annonce « dialogue » sans dire lequel.
 * @param {boolean} portal        Rend l'overlay dans document.body. À activer
 *                                quand la modale est montée sous un ancêtre à
 *                                transform / filter / backdrop-filter (ex. le
 *                                header à backdrop-blur), qui ferait du
 *                                `position: fixed` un positionnement local et
 *                                rognerait la modale. Défaut false.
 */
// Verrou de défilement à compteur. Sans lui, la dernière modale fermée
// restaure la valeur d'overflow qu'elle avait lue à SON ouverture : quand deux
// modales se chevauchent, celle du dessous rend le défilement à la page alors
// qu'elle est encore ouverte.
let scrollLockCount = 0;
let previousBodyOverflow = "";

function lockBodyScroll() {
    if (typeof document === "undefined") return;
    if (scrollLockCount === 0) {
        previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
    }
    scrollLockCount += 1;
}

function unlockBodyScroll() {
    if (typeof document === "undefined") return;
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0) {
        document.body.style.overflow = previousBodyOverflow;
    }
}

export default function BottomSheetModal({
    isOpen,
    onClose,
    children,
    maxWidthClass = "sm:max-w-md",
    panelClassName = "",
    showHandle = true,
    closeOnBackdrop = true,
    zIndexClass = "z-50",
    labelledBy,
    portal = false,
}) {
    const sentinelRef = useRef(null);
    const [portalReady, setPortalReady] = useState(false);
    const [portalContext, setPortalContext] = useState(null);

    // createPortal n'existe pas côté serveur : on attend le montage client.
    useEffect(() => {
        if (portal) setPortalReady(true);
    }, [portal]);

    // Le portail détache la modale de son point de montage : on y relit le
    // thème (la classe `dark` vit sur un wrapper du layout, pas sur <html>)
    // et la police héritée, pour les reproduire sur l'overlay.
    useIsomorphicLayoutEffect(() => {
        if (!portal || !isOpen) return;
        const sentinel = sentinelRef.current;
        if (!sentinel) return;
        setPortalContext({
            isDark: Boolean(sentinel.closest(".dark")),
            fontFamily: window.getComputedStyle(sentinel).fontFamily,
        });
    }, [portal, isOpen]);

    // Fermeture au clavier + verrouillage du scroll de la page tant qu'ouverte.
    useEffect(() => {
        if (!isOpen) return;
        const onKeyDown = (event) => {
            if (event.key === "Escape") onClose?.();
        };
        document.addEventListener("keydown", onKeyDown);
        lockBodyScroll();
        return () => {
            document.removeEventListener("keydown", onKeyDown);
            unlockBodyScroll();
        };
    }, [isOpen, onClose]);

    const overlay = (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    key="bottom-sheet-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    onClick={closeOnBackdrop ? () => onClose?.() : undefined}
                    className={`fixed inset-0 ${zIndexClass} flex items-end justify-center overflow-y-auto bg-grayDark-900/70 p-0 backdrop-blur-[6px] sm:items-center sm:p-6 ${
                        portal && portalContext?.isDark ? "dark" : ""
                    }`}
                    style={
                        portal && portalContext
                            ? { fontFamily: portalContext.fontFamily }
                            : undefined
                    }
                >
                    <motion.div
                        initial={{ y: "100%", opacity: 0.6 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: "100%", opacity: 0 }}
                        transition={{
                            type: "spring",
                            damping: 32,
                            stiffness: 380,
                            mass: 0.75,
                        }}
                        onClick={(event) => event.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={labelledBy}
                        // tabIndex -1 : le panneau peut recevoir le focus par
                        // programme sans entrer dans l'ordre de tabulation.
                        tabIndex={-1}
                        // max-h : la feuille ne dépasse jamais l'écran. Sur mobile,
                        // au minimum 15px entre le haut de l'écran et le haut de la
                        // modale ; sur desktop, on réserve le padding de l'overlay
                        // (sm:p-6 = 3rem au total). Le contenu scrolle à l'intérieur.
                        className={`relative max-h-[calc(100dvh-15px)] w-full overflow-y-auto rounded-t-[28px] bg-white shadow-2xl dark:bg-bgDark-800 sm:mx-4 sm:max-h-[calc(100dvh-3rem)] sm:rounded-3xl ${maxWidthClass} ${panelClassName}`}
                    >
                        {showHandle && (
                            <div className="flex justify-center pt-3 sm:hidden">
                                <div className="h-[3px] w-9 rounded-full bg-gray-200 dark:bg-white/15" />
                            </div>
                        )}
                        {children}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );

    if (!portal) return overlay;

    return (
        <>
            {/* Sentinelle invisible : sert uniquement à lire le contexte
                (thème, police) à l'emplacement d'origine de la modale. */}
            <span ref={sentinelRef} hidden />
            {portalReady ? createPortal(overlay, document.body) : null}
        </>
    );
}
